/**
 * Source registry and the graceful degradation ladder (§4.6, §6).
 *
 * Every external source is an adapter behind a common interface, and adding a
 * new one means touching exactly one new file plus one entry here (§6).
 *
 * The ladder is the important part:
 *
 *   BYOR (user's source) → FFC ADP (/api/adp) → FantasyCalc (browser-direct)
 *                        → Sleeper search_rank (already cached)
 *
 * The tool must always produce a usable board. If a source is unavailable it
 * drops to the next rung, says which rung it is on, and keeps going. It must
 * **never** start up with an empty board — and because the last rung reads from
 * the player cache the app already holds, it cannot, even with no network at
 * all.
 */

import type { LeagueConfig, PlayerUniverse, RankingSource } from '../core/types.ts';
import { PlayerIndex } from '../core/matching.ts';
import { sleeperRankingSource } from './sleeper.ts';
import { loadFantasyCalc, reconcileWithUniverse, FANTASYCALC_SOURCE_ID } from './fantasycalc.ts';
import { adpFormatFor, adpRankingSource, backfillByeWeeks, loadAdp, ADP_SOURCE_ID } from './adp.ts';
import type { AdpData } from '../core/types.ts';

export type SourceStatus = 'ok' | 'degraded' | 'failed' | 'disabled' | 'skipped';

export interface SourceReport {
  source_id: string;
  source_name: string;
  status: SourceStatus;
  rows: number;
  /** Present when status is not 'ok'. Written for a human, not a log parser. */
  message: string | null;
  /** Which rung of the §4.6 ladder this is. Lower is more preferred. */
  rung: number;
}

export interface LoadResult {
  sources: RankingSource[];
  adp: AdpData | null;
  reports: SourceReport[];
  /** The best rung that actually loaded. Rendered in the UI footer. */
  active_rung: number;
  /** True when nothing but the offline fallback is available. */
  offline: boolean;
}

export interface LoadOptions {
  universe: PlayerUniverse;
  index: PlayerIndex;
  league: LeagueConfig;
  /** Sources the user has already imported via BYOR (rung 0). */
  userSources?: RankingSource[];
  signal?: AbortSignal;
  /** Set false to skip network sources entirely (offline / replay mode). */
  network?: boolean;
  adpEndpoint?: string;
}

/**
 * Load every available ranking source, in ladder order, tolerating failure at
 * every rung.
 *
 * Rungs are loaded concurrently rather than sequentially — waiting for a
 * timeout on rung 1 before starting rung 3 would cost ten seconds of a
 * 60-second time-to-value budget (NFR-8), and the ladder is about *which
 * source the board defaults to*, not about the order requests are issued.
 */
export async function loadAllSources(opts: LoadOptions): Promise<LoadResult> {
  const { universe, index, league } = opts;
  const network = opts.network !== false;
  const reports: SourceReport[] = [];
  const sources: RankingSource[] = [];

  // --- Rung 3: Sleeper's own ordering. Always available, always first to
  // load, because it guarantees the board is never empty (§4.3.1).
  const sleeper = sleeperRankingSource(universe);
  sources.push(sleeper);
  reports.push({
    source_id: sleeper.source_id,
    source_name: sleeper.source_name,
    status: 'ok',
    rows: sleeper.rows.length,
    message: null,
    rung: 3,
  });

  // --- Rung 0: whatever the user brought.
  for (const source of opts.userSources ?? []) {
    sources.push(source);
    reports.push({
      source_id: source.source_id,
      source_name: source.source_name,
      status: 'ok',
      rows: source.rows.length,
      message: source.unmatched.length
        ? `${source.unmatched.length} rows could not be matched to a player.`
        : null,
      rung: 0,
    });
  }

  let adp: AdpData | null = null;

  if (network) {
    const [adpResult, fcResult] = await Promise.allSettled([
      loadAdp({
        teams: league.teams,
        season: league.season,
        format: adpFormatFor(league.ppr_value),
        index,
        signal: opts.signal,
        endpoint: opts.adpEndpoint,
      }),
      loadFantasyCalc({
        teams: league.teams,
        ppr: league.ppr_value,
        numQbs: league.superflex ? 2 : 1,
        signal: opts.signal,
      }),
    ]);

    // --- Rung 1: FFC ADP.
    if (adpResult.status === 'fulfilled') {
      adp = adpResult.value;
      const filled = backfillByeWeeks(universe, adp);
      const source = adpRankingSource(adp);
      sources.push(source);
      reports.push({
        source_id: ADP_SOURCE_ID,
        source_name: source.source_name,
        status: 'ok',
        rows: source.rows.length,
        message: filled > 0 ? `Backfilled ${filled} bye weeks.` : null,
        rung: 1,
      });
    } else {
      reports.push({
        source_id: ADP_SOURCE_ID,
        source_name: 'FFC ADP',
        status: 'failed',
        rows: 0,
        message: describeFailure(
          adpResult.reason,
          'ADP is unavailable — the /api/adp Worker may be disabled or removed. "Will he last?" estimates are off; everything else still works.',
        ),
        rung: 1,
      });
    }

    // --- Rung 2: FantasyCalc.
    if (fcResult.status === 'fulfilled') {
      const source = reconcileWithUniverse(fcResult.value, universe);
      sources.push(source);
      reports.push({
        source_id: FANTASYCALC_SOURCE_ID,
        source_name: source.source_name,
        status: 'ok',
        rows: source.rows.length,
        message: null,
        rung: 2,
      });
    } else {
      reports.push({
        source_id: FANTASYCALC_SOURCE_ID,
        source_name: 'FantasyCalc market value',
        status: 'failed',
        rows: 0,
        message: describeFailure(fcResult.reason, 'FantasyCalc is unavailable.'),
        rung: 2,
      });
    }
  } else {
    for (const [id, name, rung] of [
      [ADP_SOURCE_ID, 'FFC ADP', 1],
      [FANTASYCALC_SOURCE_ID, 'FantasyCalc market value', 2],
    ] as const) {
      reports.push({
        source_id: id,
        source_name: name,
        status: 'skipped',
        rows: 0,
        message: 'Offline mode — network sources were not contacted.',
        rung,
      });
    }
  }

  const okRungs = reports.filter((r) => r.status === 'ok').map((r) => r.rung);
  const active_rung = okRungs.length ? Math.min(...okRungs) : 3;

  return {
    sources,
    adp,
    reports: reports.sort((a, b) => a.rung - b.rung),
    active_rung,
    offline: okRungs.every((r) => r === 3),
  };
}

function describeFailure(reason: unknown, fallback: string): string {
  if (reason instanceof Error && reason.message) return `${fallback} (${reason.message})`;
  return fallback;
}

/** Human label for the current ladder rung, for the UI footer (§4.6). */
export function rungLabel(rung: number): string {
  switch (rung) {
    case 0: return 'your own ranking source';
    case 1: return 'FFC ADP';
    case 2: return 'FantasyCalc market value';
    default: return 'Sleeper popularity (offline fallback)';
  }
}

/**
 * FR-45 / FR-4: is any source stale enough to warn about?
 *
 * §4.2 requires a warning when the active source is more than 7 days old, which
 * matters most for hand-imported lists — an analyst who updates constantly is
 * exactly the case where a week-old copy is misleading.
 */
export function stalenessWarnings(sources: RankingSource[], now = new Date()): string[] {
  const out: string[] = [];
  for (const source of sources) {
    const asOf = new Date(source.as_of_date);
    if (Number.isNaN(asOf.getTime())) continue;
    const days = Math.floor((now.getTime() - asOf.getTime()) / 86_400_000);
    if (days > 7) {
      out.push(`${source.source_name} is ${days} days old (as of ${source.as_of_date}).`);
    }
  }
  return out;
}
